import { FC, ReactNode } from 'react'
import { t } from 'i18next'
import { AutoComplete, TextField } from '@microservices/wiskey-react-components'
import {
  Check as CheckIcon,
  Clear as ClearIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignRight as AlignRightIcon,
} from '@mui/icons-material'
import { Box, IconButton, Popover } from '@mui/material'

import { FORM_ELEMENT_TEXT_ALIGN } from '@constants'
import { AutocompleteOption } from '@types'

import { highlightActiveButton } from './helpers'

type TextPopoverProps = {
  isPopoverOpen: boolean
  popoverAnchorEl: HTMLElement | null
  handlePopoverClose: () => void
  textTitle: string
  textAlign?: FORM_ELEMENT_TEXT_ALIGN
  actionOptions: AutocompleteOption[]
  action: AutocompleteOption | null
  staticTextTitle?: string
  handleSaveText: () => void
  handleCancelPoppedEdit: () => void
  handleSetText: (value: string) => void
  handleSetTextAlign?: (value: FORM_ELEMENT_TEXT_ALIGN) => void
  handleSetAction?: (value: AutocompleteOption) => void
  displayAlignmentSettings?: boolean
  displayActions?: boolean
  isDisableSaveTextButton?: boolean
}

export const TextPopover: FC<TextPopoverProps> = ({
  isPopoverOpen,
  popoverAnchorEl,
  handlePopoverClose,
  textTitle,
  textAlign,
  staticTextTitle,
  handleSaveText,
  handleCancelPoppedEdit,
  handleSetText,
  handleSetTextAlign,
  handleSetAction,
  displayAlignmentSettings = false,
  displayActions = false,
  action,
  actionOptions,
  isDisableSaveTextButton = false,
}) => {
  const handleCancel = () => {
    handleCancelPoppedEdit()
    handleSetText(staticTextTitle ?? '')
  }

  const handleEditAlignment = (newValue: FORM_ELEMENT_TEXT_ALIGN) => {
    handleSetTextAlign?.(newValue)
  }

  return (
    <Popover
      anchorEl={popoverAnchorEl}
      id={'edit-item-popover'}
      open={isPopoverOpen}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      onClick={e => e.stopPropagation()}
      onClose={handlePopoverClose}
    >
      <Box sx={{ m: 1 }}>
        <Box>
          <TextField
            autoFocus
            placeholder={t('fieldsConfig.placeholder.text')}
            size={'small'}
            sx={{ ml: 1 }}
            value={textTitle}
            variant={'standard'}
            onChange={event => handleSetText(event.target.value)}
          />
          <IconButton disabled={!textTitle || isDisableSaveTextButton} onClick={handleSaveText}>
            <CheckIcon />
          </IconButton>
          <IconButton onClick={handleCancel}>
            <ClearIcon />
          </IconButton>
        </Box>
        {displayAlignmentSettings && (
          <Box>
            <IconButton
              sx={{ ...highlightActiveButton(textAlign === FORM_ELEMENT_TEXT_ALIGN.LEFT) }}
              onClick={() => handleEditAlignment(FORM_ELEMENT_TEXT_ALIGN.LEFT)}
            >
              <AlignLeftIcon />
            </IconButton>
            <IconButton
              sx={{ ...highlightActiveButton(textAlign === FORM_ELEMENT_TEXT_ALIGN.CENTER) }}
              onClick={() => handleEditAlignment(FORM_ELEMENT_TEXT_ALIGN.CENTER)}
            >
              <AlignCenterIcon />
            </IconButton>
            <IconButton
              sx={{ ...highlightActiveButton(textAlign === FORM_ELEMENT_TEXT_ALIGN.RIGHT) }}
              onClick={() => handleEditAlignment(FORM_ELEMENT_TEXT_ALIGN.RIGHT)}
            >
              <AlignRightIcon />
            </IconButton>
          </Box>
        )}
        {displayActions && (
          <Box>
            <AutoComplete
              options={actionOptions}
              size='small'
              sx={{ ml: 1, mr: 1 }}
              value={action}
              renderInput={params => {
                return (
                  <TextField
                    {...params}
                    inputProps={{ ...params.inputProps }}
                    variant='outlined'
                    label={
                      t(`fieldsConfig.${action ? 'helperText' : 'placeholder'}.action`) as ReactNode
                    }
                  />
                )
              }}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )
              }}
              onChange={handleSetAction}
            />
          </Box>
        )}
      </Box>
    </Popover>
  )
}
